import { Box, Typography, IconButton, Menu, Badge } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import GroupedNotifications from "@/modules/notification-center/components/notification/grouped-notifications";
import { PathConstants } from "@/routes/constants";
import { useNavigate } from "react-router-dom";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import { NwButton } from "@nawy/ui";

interface NotificationMenuProps {
  anchorEl: HTMLElement | null;
  handleCloseNotificationBell: () => void;
  unreadCount?: number;
}

export const NotificationsLayover: React.FC<NotificationMenuProps> = ({
  anchorEl,
  handleCloseNotificationBell,
  unreadCount = 0,
}) => {
  const navigate = useNavigate();

  return (
    <Menu
      open={Boolean(anchorEl)}
      onClose={handleCloseNotificationBell}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{
        sx: {
          width: 420,
          maxHeight: 650,
          borderRadius: nawyTheme.nawy.radius.xl,
          overflow: "hidden",
          boxShadow: nawyTheme.nawy.elevation["2xl"],
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          bgcolor: nawyTheme.nawy.colors.semantic.layer.primary,
          zIndex: 1,
          px: nawyTheme.nawy.spacing.lg,
          py: nawyTheme.nawy.spacing.md,
          borderBottom: `1px solid ${nawyTheme.nawy.colors.semantic.border.primary}`,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box
            display="flex"
            alignItems="center"
            gap={nawyTheme.nawy.spacing.sm}
          >
            <Badge
              badgeContent={unreadCount}
              color="error"
              max={99}
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "0.7rem",
                  minWidth: "17px",
                  height: "17px",
                  bgcolor:
                    nawyTheme.nawy.colors.semantic.layer.extension.negative,
                  color: nawyTheme.nawy.colors.semantic.text.onColor,
                },
              }}
            >
              <NotificationsIcon
                sx={{
                  color: nawyTheme.nawy.colors.semantic.layer.accent,
                  fontSize: 24,
                }}
              />
            </Badge>

            <Typography
              sx={{
                ...nawyTheme.nawy.typography.english.variants.headline.xs,
              }}
            >
              Notifications
            </Typography>
          </Box>

          <IconButton
            onClick={handleCloseNotificationBell}
            size="small"
            sx={{
              transition: "all 0.2s",
              "&:hover": {
                bgcolor: nawyTheme.nawy.colors.semantic.state.onDark08,
                transform: "rotate(90deg)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          p: nawyTheme.nawy.spacing.xs,
          overflowY: "auto",
          maxHeight: 480,
          "&::-webkit-scrollbar": { width: nawyTheme.nawy.spacing.xxs },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: nawyTheme.nawy.colors.semantic.state.onDark12,
            borderRadius: nawyTheme.nawy.radius.full,
            "&:hover": {
              background: nawyTheme.nawy.colors.semantic.state.onDark16,
            },
          },
        }}
      >
        <GroupedNotifications maxToRender={10} />
      </Box>

      {/* Footer */}
      <Box
        sx={{
          position: "sticky",
          bgcolor: nawyTheme.nawy.colors.semantic.layer.primary,
          borderTop: `1px solid ${nawyTheme.nawy.colors.semantic.border.primary}`,
          px: nawyTheme.nawy.spacing.lg,
          py: nawyTheme.nawy.spacing.md,
        }}
      >
        <NwButton
          fullWidth
          onClick={() => {
            navigate(PathConstants.NOTIFICATIONS_CENTER);
            handleCloseNotificationBell();
          }}
        >
          <Box
            sx={{
              gap: nawyTheme.nawy.spacing.xs,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                ...nawyTheme.nawy.typography.english.variants.body.xs,
                fontWeight: 600,
              }}
            >
              View All Notifications
            </Typography>
            <ArrowForwardIcon sx={{ fontSize: 18 }} />
          </Box>
        </NwButton>
      </Box>
    </Menu>
  );
};
